/**
 * Modal for linking an account as a subsidiary
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label } from 'flowbite-react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { accountApi, type Account } from '../../../../../_shared/lib/frontend/api';

interface SubsidiaryLinkModalProps {
  parentAccountId: string;
  onClose: () => void;
}

export function SubsidiaryLinkModal({
  parentAccountId,
  onClose,
}: SubsidiaryLinkModalProps) {
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['accounts', 'available-for-linking'],
    queryFn: () => accountApi.list({ page: 1, per_page: 100 }),
  });

  const linkMutation = useMutation({
    mutationFn: () =>
      accountApi.linkSubsidiary(parentAccountId, selectedAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsidiaries', parentAccountId] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['hierarchy', parentAccountId] });
      onClose();
    },
  });

  const availableAccounts =
    data?.accounts?.filter(
      (a: Account) =>
        a.id !== parentAccountId && !a.parent_account_id
    ) ?? [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedAccountId) linkMutation.mutate();
  };

  return (
    <Modal show onClose={onClose} size="md">
      <ModalHeader>Link Subsidiary Account</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="subsidiary-select">Select Account to Link</Label>
              <select
                id="subsidiary-select"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Choose an account...</option>
                {availableAccounts.map((a: Account) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Only top-level accounts can be linked as subsidiaries
              </p>
            </div>

            {availableAccounts.length === 0 && !isLoading && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  No accounts available to link. All existing accounts either
                  already have parent relationships or cannot be linked.
                </p>
              </div>
            )}

            {linkMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                Failed to link subsidiary. This might create a circular
                hierarchy.
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="blue"
            disabled={!selectedAccountId || linkMutation.isPending || isLoading}
          >
            {linkMutation.isPending ? 'Linking...' : 'Link Subsidiary'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
